import React from 'react';
import { motion } from 'motion/react';
import { BarChart3, FileText, Users, Zap } from 'lucide-react';

const PROMPTS = [
  {
    icon: Zap,
    label: 'Velocity',
    title: 'Sprint velocity',
    prompt: 'What is our PR merge rate this sprint compared to last sprint?',
    color: 'text-amber-400',
    bg: 'bg-amber-400/10 border-amber-400/20 hover:border-amber-400/40 hover:bg-amber-400/15',
  },
  {
    icon: Users,
    label: 'People',
    title: 'Top contributors',
    prompt: 'Who are the top contributors this week and what did they work on?',
    color: 'text-brand-teal',
    bg: 'bg-brand-teal/10 border-brand-teal/20 hover:border-brand-teal/40 hover:bg-brand-teal/15',
  },
  {
    icon: FileText,
    label: 'Brief',
    title: 'Morning standup',
    prompt: 'Generate a morning standup brief covering recent activity across all streams.',
    color: 'text-brand-purple-light',
    bg: 'bg-brand-purple/10 border-brand-purple/20 hover:border-brand-purple/40 hover:bg-brand-purple/15',
  },
  {
    icon: BarChart3,
    label: 'Health',
    title: 'Open alerts',
    prompt: 'Summarise open alerts, failing checks, and blockers across the organisation.',
    color: 'text-vivid-red',
    bg: 'bg-vivid-red/10 border-vivid-red/20 hover:border-vivid-red/40 hover:bg-vivid-red/15',
  },
];

function greeting(username?: string) {
  const h = new Date().getHours();
  const time = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
  return username ? `Good ${time}, ${username}.` : `Good ${time}.`;
}

interface VantiWindowEmptyStateProps {
  username?: string;
  onSend: (text: string) => void;
}

export default function VantiWindowEmptyState({ username, onSend }: VantiWindowEmptyStateProps) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="w-full max-w-xl"
      >
        {/* Logo + greeting */}
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img
            src="/assets/Vanti2.png"
            alt=""
            className="h-14 w-14 object-contain opacity-90"
            aria-hidden
          />
          <h1 className="text-2xl font-semibold text-slate-100">{greeting(username)}</h1>
          <p className="text-sm text-slate-500">Ask me anything about your team, code, or projects.</p>
        </div>

        {/* Quick-start cards */}
        <div className="grid grid-cols-2 gap-3">
          {PROMPTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.button
                key={p.label}
                type="button"
                onClick={() => onSend(p.prompt)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 + i * 0.06, ease: 'easeOut' }}
                className={`flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all ${p.bg}`}
              >
                <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${p.bg.split(' ')[0]}`}>
                  <Icon className={`h-4 w-4 ${p.color}`} aria-hidden />
                </span>
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-wider ${p.color}`}>{p.label}</p>
                  <p className="mt-0.5 text-sm font-medium text-slate-200">{p.title}</p>
                  <p className="mt-1 text-xs text-slate-500 leading-snug line-clamp-2">{p.prompt}</p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}
